import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { MessageSquareQuote, Phone, MapPin, Calendar as CalendarIcon, Users, Sparkles } from "lucide-react";

const FILTERS = [
  { key: "enquiry", label: "New Enquiries" },
  { key: "pending", label: "Quoted / Awaiting" },
  { key: "confirmed", label: "Confirmed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

const statusBadge = (s) => {
  const map = {
    enquiry: "bg-amber-100 text-amber-800 border-amber-300",
    pending: "bg-indigo-100 text-indigo-800 border-indigo-300",
    confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
    cancelled: "bg-rose-100 text-rose-800 border-rose-300",
  };
  return map[s] || "bg-slate-100 text-slate-700 border-slate-300";
};

export default function AdminLeads() {
  const [filter, setFilter] = useState("enquiry");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(null);
  const [quote, setQuote] = useState({ fare: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leads?status_filter=${filter}`);
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const sendQuote = async () => {
    const fare = parseFloat(quote.fare);
    if (!fare || fare <= 0) { toast.error("Enter a valid fare"); return; }
    try {
      await api.patch(`/bookings/${quoteOpen.booking_id}/quote`, { fare, notes: quote.notes });
      toast.success(`Quote of ₹${fare.toLocaleString()} sent to ${quoteOpen.customer_name}`);
      setQuoteOpen(null);
      setQuote({ fare: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send quote"); }
  };

  const cancel = async (b) => {
    if (!window.confirm(`Cancel enquiry ${b.booking_id}?`)) return;
    try {
      await api.patch(`/bookings/${b.booking_id}/status`, { status: "cancelled" });
      toast.success("Lead cancelled");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-black tracking-tight flex items-center gap-2">
            <MessageSquareQuote className="text-[#ea580c]" /> Custom Booking Leads
          </h1>
          <p className="text-sm text-slate-600 mt-1">Enquiries from the Custom Trip form — negotiate rates and send quotes via WhatsApp.</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="leads-refresh">Refresh</Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="leads-filter-tabs">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`leads-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === f.key
                ? "bg-[#1e1b4b] text-white border-[#1e1b4b]"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-10 rounded-2xl bg-white border border-slate-200 text-center text-slate-500" data-testid="leads-empty">
            <Sparkles className="w-8 h-8 mx-auto text-[#ea580c] opacity-60" />
            <div className="mt-2 font-semibold text-slate-700">No leads in this pipeline</div>
            <div className="text-xs mt-1">Custom enquiries submitted from the public form appear here.</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(b => (
            <div key={b.booking_id} data-testid={`lead-${b.booking_id}`} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-mono text-xs text-slate-500">{b.booking_id}</div>
                  <div className="font-display font-bold text-lg text-[#1e1b4b] mt-0.5">{b.customer_name}</div>
                  <a href={`tel:${b.customer_mobile}`} className="text-xs text-slate-600 flex items-center gap-1 mt-0.5">
                    <Phone className="w-3 h-3" /> {b.customer_mobile}
                  </a>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${statusBadge(b.status)}`}>{b.status}</span>
              </div>

              <div className="text-sm text-slate-700 space-y-1">
                <div className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#ea580c]" /> <b>{b.from_place}</b> → <b>{b.to_place}</b></div>
                <div className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5 text-slate-500" /> {b.travel_date}{b.return_date ? ` → ${b.return_date}` : ""} {b.departure_time && <span className="text-slate-500 text-xs">· {b.departure_time}</span>}</div>
                <div className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-500" /> {b.passengers} pax · {b.ac ? "AC" : "Non-AC"} · {b.vehicle_type_pref || "Any vehicle"}</div>
              </div>

              {(b.pickup_location || b.drop_location) && (
                <div className="text-xs text-slate-500 border-l-2 border-slate-200 pl-2">
                  {b.pickup_location && <div>📍 Pickup: {b.pickup_location}</div>}
                  {b.drop_location && <div>🏁 Drop: {b.drop_location}</div>}
                </div>
              )}

              {b.notes && (
                <div className="text-xs italic text-slate-600 bg-slate-50 rounded-lg p-2 border border-slate-100">"{b.notes}"</div>
              )}

              {b.quoted_fare > 0 && (
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-emerald-800">
                  <b>Quoted: ₹{Number(b.quoted_fare).toLocaleString()}</b>
                  {b.quote_notes && <span className="block text-emerald-700 mt-0.5">{b.quote_notes}</span>}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                {(b.status === "enquiry" || b.status === "pending") && (
                  <Button size="sm" className="bg-[#ea580c] hover:bg-orange-700 flex-1"
                    onClick={() => { setQuote({ fare: b.quoted_fare || "", notes: b.quote_notes || "" }); setQuoteOpen(b); }}
                    data-testid={`lead-quote-${b.booking_id}`}>
                    {b.status === "enquiry" ? "Send Quote" : "Update Quote"}
                  </Button>
                )}
                <a href={`https://wa.me/${(b.customer_whatsapp || b.customer_mobile || "").replace(/\D/g, "")}`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center justify-center text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                   data-testid={`lead-wa-${b.booking_id}`}>WhatsApp</a>
                {b.status !== "cancelled" && b.status !== "confirmed" && (
                  <Button size="sm" variant="outline" onClick={() => cancel(b)} data-testid={`lead-cancel-${b.booking_id}`}>Cancel</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!quoteOpen} onOpenChange={o => !o && setQuoteOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Quote — {quoteOpen?.booking_id}</DialogTitle></DialogHeader>
          {quoteOpen && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs">
                <b>{quoteOpen.customer_name}</b> · {quoteOpen.customer_mobile}<br />
                {quoteOpen.from_place} → {quoteOpen.to_place} · {quoteOpen.travel_date} · {quoteOpen.passengers} pax · {quoteOpen.ac ? "AC" : "Non-AC"} · {quoteOpen.vehicle_type_pref || "Any"}<br />
                {quoteOpen.notes && <span className="text-slate-500">Customer notes: {quoteOpen.notes}</span>}
              </div>
              <div>
                <label className="text-sm font-semibold">Quoted Fare (₹) *</label>
                <Input type="number" min="1" value={quote.fare} onChange={e => setQuote({ ...quote, fare: e.target.value })} placeholder="e.g. 8500" data-testid="lead-quote-fare-input" />
              </div>
              <div>
                <label className="text-sm font-semibold">Notes (optional)</label>
                <Textarea value={quote.notes} onChange={e => setQuote({ ...quote, notes: e.target.value })} placeholder="Includes toll + driver allowance. Non-AC option available at ₹500 less." data-testid="lead-quote-notes-input" />
              </div>
              <div className="text-xs text-slate-500">💬 A WhatsApp message with the quote will be sent to the customer.</div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={sendQuote} className="bg-[#ea580c] hover:bg-orange-700" data-testid="lead-quote-send">Send Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
